import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ComposedChart, Line, LineChart, ReferenceLine, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDownRight, ArrowUpRight, Lock, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';

/* W2.4 Fortschritt-Tab der Kundenakte (29.10.2026, Doku docs/coach-cockpit/w2-fortschritt.md).
   Gerechnet wird NUR auf dem Server (`fortschritt.fortschritt_sync`): Zeitraum, Statusleiste, Verläufe, Bilanz zur
   Vorperiode, Heatmap je Kalenderwoche, Bildvergleich. Hier wird nur gezeichnet.

   Schnittstelle (wiederverwendbar im CRM und auf /coach):
     leadId       Lead-ID des Mitglieds
     client       { laden(leadId, params) → axios-Antwort, foto(leadId, fid, klein) → axios-Antwort (Blob) }
                  Standard: CRM (`crmFortschrittClient`); auf /coach: `coachFortschrittClient(coachAxios)`
     fuerTrainer  zeichnet die Trendlinie nach vorne, wenn der Server sie liefert (`rechte.trend`). Der Kunde sieht nie
                  eine Prognose — eine App-Ansicht übergibt false (der Server liefert dann ohnehin keinen Trend).
   Fotos kommen als Blob mit Anmeldung, nie über eine öffentliche Adresse. */

export const crmFortschrittClient = {
  laden: (leadId, params) => api.get(`/ernaehrung/mitglied/${leadId}/fortschritt`, { params }),
  foto: (leadId, fid, klein) => api.get(`/ernaehrung/mitglied/${leadId}/fortschrittsfoto/${fid}`, { params: klein ? { klein: 1 } : {}, responseType: 'blob', timeout: 30000 }),
};

/** Für /coach: `coach` ist der axios-Client der Trainer-Seite (baseURL /api/coach, Trainer-Token im Header). Fotos über den
 *  Ernährungs-Spiegel (nur mit Schalter „Ernährung“ — ohne ihn liefert der Server ohnehin keine Bilder). */
export const coachFortschrittClient = (coach) => ({
  laden: (leadId, params) => coach.get(`/kunden/${leadId}/fortschritt`, { params }),
  foto: (leadId, fid, klein) => coach.get(`/x/ernaehrung/mitglied/${leadId}/fortschrittsfoto/${fid}`, { params: klein ? { klein: 1 } : {}, responseType: 'blob', timeout: 30000 }),
});

/* Auf /coach (Prop `coachStil`) im Stil des Mockups „Fortschritt“: aktiver Chip in der Markenfarbe, Heatmap in Marken-Stufen.
   Im CRM bleibt alles wie vorher. */
const CoachStil = createContext(false);
const ZEITRAEUME = [['7t', '7T'], ['4w', '4W'], ['12w', '12W'], ['6m', '6M'], ['max', 'Max']];
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const tag = (iso, opt = { day: '2-digit', month: '2-digit' }) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', opt) : '');
const ms = (iso) => new Date(`${String(iso).slice(0, 10)}T12:00:00`).getTime();
const zahl = (v, dez = 1) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('de-DE', { minimumFractionDigits: dez, maximumFractionDigits: dez }));
const vz = (v, dez = 1) => (v === null || v === undefined ? '' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${zahl(Math.abs(v), dez)}`);
const farbeText = (besser) => (besser === true ? 'text-emerald-700 dark:text-emerald-400' : besser === false ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground');
const token = (name) => { try { return `hsl(${getComputedStyle(document.documentElement).getPropertyValue(name).trim()})`; } catch { return 'currentColor'; } };
const QUELLE_FORM = { tageseintrag: 'circle', checkin: 'diamond', inbody: 'square', checkup: 'triangle' };
const QUELLE_TOKEN = { tageseintrag: '--muted-foreground', checkin: '--chart-2', inbody: '--chart-4', checkup: '--chart-5' };
const ZUSTAND_KLASSE = {
  absolviert: 'bg-marke text-white',
  teilweise: 'bg-marke/40',
  kurs: 'bg-sky-600/70 text-white',
  frei: 'bg-sky-500/25',
};
const tooltipStil = { borderRadius: 12, fontSize: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--foreground))' };

function Pille({ an, children, onClick, label }) {
  const coach = useContext(CoachStil);
  if (coach) {
    return (
      <button type="button" aria-pressed={an} aria-label={label} onClick={onClick} data-testid="fortschritt-chip"
        className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border px-[14px] text-xs font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground/80 hover:text-foreground'}`}>
        {children}
      </button>
    );
  }
  return (
    <button type="button" aria-pressed={an} aria-label={label} onClick={onClick}
      className={`min-h-[44px] min-w-[44px] rounded-full border px-4 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'bg-foreground text-background border-foreground font-semibold' : 'bg-background hover:bg-muted'}`}>
      {children}
    </button>
  );
}

function Pfeil({ besser, delta }) {
  if (delta === null || delta === undefined) return null;
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  return <Icon className={`inline h-4 w-4 ${farbeText(besser)}`} aria-hidden="true" />;
}

function Kachel({ titel, wert, sub, delta, besser, gesperrt, testId }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-3" data-testid={testId}>
      <span className="block text-xs text-muted-foreground">{titel}</span>
      {gesperrt ? (
        <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />nur mit Ernährungs-Recht</span>
      ) : (
        <>
          <span className="block text-xl font-semibold tabular-nums">{wert}</span>
          {(sub || delta) && <span className={`block text-xs font-medium ${farbeText(besser)}`}><Pfeil besser={besser} delta={delta?.zahl} />{delta?.text}{sub ? <span className="text-muted-foreground font-normal">{delta ? ' · ' : ''}{sub}</span> : null}</span>}
        </>
      )}
    </div>
  );
}

/* ── Statusleiste ── */
function Statusleiste({ s, messwerte }) {
  const g = s.gewicht, u = s.umfaenge, t = s.training, e = s.ernaehrung, c = s.checkin_treue;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="fortschritt-status">
      <Kachel testId="status-gewicht" titel="Gewicht" gesperrt={!messwerte}
        wert={g?.aktuell == null ? '—' : `${zahl(g.aktuell)} kg`} besser={g?.besser}
        delta={g?.delta != null ? { zahl: g.delta, text: `${vz(g.delta)} kg` } : null} sub={g?.datum ? `Stand ${tag(g.datum)}` : 'keine Messung'} />
      <Kachel testId="status-umfaenge" titel="Umfänge (Summe Δ)" gesperrt={!messwerte}
        wert={u?.delta_summe == null ? '—' : `${vz(u.delta_summe)} cm`} besser={u?.besser}
        sub={u?.stellen?.length ? u.stellen.map((x) => `${x.name} ${x.delta == null ? '—' : vz(x.delta)}`).join(' · ') : 'keine Messung'} />
      <Kachel testId="status-training" titel="Training (Plan erfüllt)"
        wert={t.quote == null ? '—' : `${t.quote} %`} sub={`${t.ist}/${t.soll} geplant · ${t.einheiten} Einheiten · ${t.studio_besuche} Studio-Tage`} />
      <Kachel testId="status-ernaehrung" titel="Ernährung (Tage im Ziel)" gesperrt={!messwerte}
        wert={e ? `${e.im_ziel}/${e.tage}` : '—'} sub={e ? `${e.quote ?? '—'} % · ${e.geloggt} Tage geloggt` : 'keine Ernährungsdaten'} />
      <Kachel testId="status-checkins" titel="Check-in-Treue" gesperrt={!messwerte}
        wert={c?.quote == null ? '—' : `${c.quote} %`} sub={c ? `${c.eingereicht} von ${c.faellig} eingereicht` : ''} />
    </div>
  );
}

/* ── Gewicht: Messpunkte nach Quelle, geglättet, Ziel, Trend (nur Trainer) ── */
function GewichtChart({ g, zeitraum, mitTrend }) {
  const farben = useMemo(() => Object.fromEntries(Object.entries(QUELLE_TOKEN).map(([q, t]) => [q, token(t)])), []);
  const marke = useMemo(() => token('--marke'), []);
  const leise = useMemo(() => token('--muted-foreground'), []);
  const vorne = useMemo(() => token('--foreground'), []);
  if (!g) return null;
  const quellen = g.quellen.filter((q) => g.punkte.some((p) => p.quelle === q.quelle));
  const trend = mitTrend && g.trend ? g.trend.punkte.map((p) => ({ t: ms(p.datum), wert: p.wert })) : null;
  const xMin = ms(zeitraum.von);
  const xMax = Math.max(ms(zeitraum.bis), ...(trend || []).map((p) => p.t));
  const werte = [...g.punkte.map((p) => p.wert), ...(trend || []).map((p) => p.wert), ...(g.ziel != null ? [g.ziel] : [])];
  return (
    <Card data-testid="fortschritt-gewicht">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">Gewicht</CardTitle>
          {mitTrend && g.trend && <Badge variant="outline" className="border-dashed">{g.trend.label}: {vz(g.trend.kg_je_woche, 2)} kg/Woche</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {g.punkte.length === 0 ? <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">Keine Messung in diesem Zeitraum.</p> : (
          <div className="h-64 w-full" role="img" aria-label={`Gewichtsverlauf mit ${g.punkte.length} Messpunkten`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                <CartesianGrid vertical={false} stroke={leise} strokeOpacity={0.15} />
                <XAxis type="number" dataKey="t" domain={[xMin, xMax]} scale="time" tickFormatter={(t) => new Date(t).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                  tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} allowDuplicatedCategory={false} />
                <YAxis type="number" dataKey="wert" domain={[Math.floor(Math.min(...werte) - 1), Math.ceil(Math.max(...werte) + 1)]} tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStil} labelFormatter={(t) => tag(new Date(t).toISOString(), { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  formatter={(w, name) => [`${zahl(w)} kg`, name]} />
                {g.ziel != null && <ReferenceLine y={g.ziel} stroke={leise} strokeDasharray="4 4" label={{ value: `Ziel ${zahl(g.ziel)} kg`, position: 'insideTopRight', fontSize: 11, fill: leise }} />}
                <Line data={g.geglaettet.map((p) => ({ t: ms(p.datum), wert: p.wert }))} dataKey="wert" name="Geglättet (7 Tage)" type="monotone" stroke={marke} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                {quellen.map((q) => (
                  <Scatter key={q.quelle} name={q.name} data={g.punkte.filter((p) => p.quelle === q.quelle).map((p) => ({ t: ms(p.datum), wert: p.wert }))}
                    dataKey="wert" fill={farben[q.quelle]} shape={QUELLE_FORM[q.quelle]} isAnimationActive={false} />
                ))}
                {trend && <Line data={trend} dataKey="wert" name={g.trend.label} type="linear" stroke={vorne} strokeWidth={1.5} strokeDasharray="6 4" dot={false} isAnimationActive={false} />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Legende Gewicht">
          <li className="flex items-center gap-1"><span className="inline-block h-0.5 w-5 rounded bg-marke" />Geglättet (gleitender 7-Tage-Mittelwert)</li>
          {quellen.map((q) => <li key={q.quelle} className="flex items-center gap-1"><svg width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="4" fill={farben[q.quelle]} /></svg>{q.name}</li>)}
          {g.ziel != null && <li>gestrichelt waagrecht = Ziel</li>}
          {trend && <li className="font-medium text-foreground">gestrichelt nach vorne = {g.trend.label}</li>}
        </ul>
      </CardContent>
    </Card>
  );
}

/* ── kleine Linien- und Balkendiagramme ── */
function Linien({ titel, reihen, einheit, testId, leer }) {
  const leise = useMemo(() => token('--muted-foreground'), []);
  const farben = useMemo(() => ['--marke', '--chart-2', '--chart-4', '--chart-5', '--chart-3'].map(token), []);
  const sichtbar = reihen.filter((r) => r.punkte.length);
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-base">{titel}</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        {sichtbar.length === 0 ? <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">{leer}</p> : <>
          <div className="h-48 w-full" role="img" aria-label={`${titel}: ${sichtbar.map((r) => r.name).join(', ')}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                <CartesianGrid vertical={false} stroke={leise} strokeOpacity={0.15} />
                <XAxis type="number" dataKey="t" domain={['dataMin', 'dataMax']} scale="time" tickFormatter={(t) => new Date(t).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} allowDuplicatedCategory={false} />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStil} labelFormatter={(t) => tag(new Date(t).toISOString())} formatter={(w, name) => [`${zahl(w)} ${einheit}`, name]} />
                {sichtbar.map((r, i) => <Line key={r.name} data={r.punkte.map((p) => ({ t: ms(p.datum), wert: p.wert }))} dataKey="wert" name={r.name} type="monotone" stroke={farben[i % farben.length]} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {sichtbar.map((r, i) => <li key={r.name} className="flex items-center gap-1"><span className="inline-block h-0.5 w-5 rounded" style={{ background: farben[i % farben.length] }} />{r.name}</li>)}
          </ul>
        </>}
      </CardContent>
    </Card>
  );
}

function TrainingWochen({ wochen }) {
  const leise = useMemo(() => token('--muted-foreground'), []);
  const marke = useMemo(() => token('--marke'), []);
  const kurs = useMemo(() => token('--chart-2'), []);
  const daten = wochen.map((w) => ({ name: w.label, Geplant: w.soll, Eingetragen: w.ist, 'Studio-Tage': w.kurs + w.frei, quote: w.quote }));
  return (
    <Card data-testid="fortschritt-training">
      <CardHeader className="pb-2"><CardTitle className="text-base">Training je Woche</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="h-48 w-full" role="img" aria-label="Training je Kalenderwoche: geplant, eingetragen, Studio-Tage">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daten} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
              <CartesianGrid vertical={false} stroke={leise} strokeOpacity={0.15} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} width={36} />
              <Tooltip contentStyle={tooltipStil} formatter={(w, name, item) => [name === 'Eingetragen' && item?.payload?.quote != null ? `${w} (${item.payload.quote} %)` : w, name]} />
              <Bar dataKey="Geplant" fill={leise} fillOpacity={0.35} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="Eingetragen" fill={marke} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="Studio-Tage" fill={kurs} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground">Geplant = fällige Einheiten des Trainingsplans · Eingetragen = davon im Kraftlog · Studio-Tage = Tage mit Check-in</p>
      </CardContent>
    </Card>
  );
}

function ErnaehrungWochen({ wochen }) {
  const leise = useMemo(() => token('--muted-foreground'), []);
  const marke = useMemo(() => token('--marke'), []);
  if (!wochen) return null;
  const daten = wochen.map((w) => ({ name: w.label, 'Tage im Ziel': w.im_ziel, Geloggt: w.geloggt }));
  return (
    <Card data-testid="fortschritt-ernaehrung">
      <CardHeader className="pb-2"><CardTitle className="text-base">Ernährung je Woche</CardTitle></CardHeader>
      <CardContent className="text-sm">
        {daten.length === 0 ? <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">Keine Ernährungsdaten in diesem Zeitraum.</p> : (
          <div className="h-48 w-full" role="img" aria-label="Ernährung je Kalenderwoche: Tage geloggt und im Ziel">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daten} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                <CartesianGrid vertical={false} stroke={leise} strokeOpacity={0.15} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} domain={[0, 7]} tick={{ fontSize: 11, fill: leise }} tickLine={false} axisLine={false} width={36} />
                <Tooltip contentStyle={tooltipStil} />
                <Bar dataKey="Geloggt" fill={leise} fillOpacity={0.35} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="Tage im Ziel" fill={marke} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Bilanz gegenüber der Vorperiode ── */
function Bilanz({ bilanz, hinweis }) {
  return (
    <Card data-testid="fortschritt-bilanz">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Bilanz</CardTitle>
        {bilanz && <p className="text-xs text-muted-foreground">gegenüber {tag(bilanz.vorperiode.von)}–{tag(bilanz.vorperiode.bis)} (gleich lang davor)</p>}
      </CardHeader>
      <CardContent className="text-sm">
        {!bilanz ? <p className="text-xs text-muted-foreground">{hinweis}</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2 font-medium">Kennzahl</th><th className="py-1 pr-2 text-right font-medium">Zeitraum</th><th className="py-1 pr-2 text-right font-medium">Davor</th><th className="py-1 text-right font-medium">Δ</th></tr></thead>
            <tbody>
              {bilanz.zeilen.map((z) => {
                const dez = z.einheit === 'kg' || z.einheit === 'cm' ? 1 : 0;
                const f = (v) => (v == null ? '—' : `${dez ? vz(v, dez) : zahl(v, 0)}${z.einheit ? ` ${z.einheit}` : ''}`);
                return (
                  <tr key={z.id} className="border-t border-border/60">
                    <td className="py-1.5 pr-2">{z.name}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-medium">{f(z.jetzt)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-muted-foreground">{f(z.vorher)}</td>
                    <td className={`py-1.5 text-right tabular-nums font-semibold ${farbeText(z.besser)}`}>{z.delta == null ? '—' : vz(z.delta, dez)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Heatmap nach Kalenderwoche ── */
const ZUSTAND_COACH = { absolviert: 'bg-marke text-[#140c08]', teilweise: 'bg-[#6b3216] text-white', kurs: 'bg-muted border-[1.5px] border-marke', frei: 'bg-muted' };
function Heatmap({ h }) {
  const coach = useContext(CoachStil);
  const ZK = coach ? ZUSTAND_COACH : ZUSTAND_KLASSE;
  return (
    <Card data-testid="fortschritt-heatmap">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2"><CardTitle className="text-base">Training & Besuche je Kalenderwoche</CardTitle>
          {h.gekuerzt && <span className="text-xs text-muted-foreground">die letzten 53 Wochen</span>}</div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="overflow-x-auto">
          <div className="grid min-w-[300px] gap-1" style={{ gridTemplateColumns: '3.5rem repeat(7, minmax(2rem, 1fr))' }} role="table" aria-label="Heatmap Training und Studio-Besuche">
            <div role="row" className="contents">
              <span role="columnheader" className="text-[11px] text-muted-foreground" />
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => <span key={w} role="columnheader" className="text-center text-[11px] text-muted-foreground">{w}</span>)}
            </div>
            {h.wochen.map((w) => (
              <div role="row" key={w.montag} className="contents">
                <span role="rowheader" className="self-center text-[11px] tabular-nums text-muted-foreground" title={`ab ${tag(w.montag)}`}>{w.label}</span>
                {w.tage.map((t) => {
                  const name = t.zustand ? h.legende.find((l) => l.zustand === t.zustand)?.name : t.zukunft ? 'noch nicht' : t.ausserhalb ? 'außerhalb des Zeitraums' : 'nichts';
                  const titel = `${t.wochentag} ${tag(t.datum, { day: '2-digit', month: '2-digit', year: '2-digit' })}: ${name}${t.kurse?.length ? ` (${t.kurse.join(', ')})` : ''}${t.einheiten > 1 ? ` · ${t.einheiten} Einheiten` : ''}`;
                  const klasse = t.zustand ? ZK[t.zustand] : t.zukunft || t.ausserhalb ? 'border border-dashed border-border/60' : 'bg-muted';
                  return <span key={t.datum} role="cell" title={titel} aria-label={titel} className={`flex h-8 items-center justify-center rounded-md text-[10px] tabular-nums ${klasse}`}>{Number(t.datum.slice(8, 10))}</span>;
                })}
              </div>
            ))}
          </div>
        </div>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="Legende Heatmap">
          {h.legende.map((l) => <li key={l.zustand} className="flex items-center gap-1"><span className={`inline-block h-3 w-3 rounded ${ZK[l.zustand]}`} />{l.name} ({h.zaehler[l.zustand]})</li>)}
          <li className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-muted" />nichts</li>
        </ul>
      </CardContent>
    </Card>
  );
}

/* ── Bildvergleich ── */
function Foto({ client, leadId, fid, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u; let weg = false;
    setUrl(null);
    if (!fid) return undefined;
    client.foto(leadId, fid, false).then((r) => { if (weg) return; u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(null));
    return () => { weg = true; if (u) URL.revokeObjectURL(u); };
  }, [client, leadId, fid]);
  if (!fid) return <div className="flex aspect-[3/4] items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">kein Foto</div>;
  return url ? <img src={url} alt={alt} className="aspect-[3/4] w-full rounded-md object-cover" /> : <Skeleton className="aspect-[3/4] w-full" />;
}

function Bildvergleich({ b, client, leadId }) {
  const [links, setLinks] = useState(b?.vergleich?.a?.datum || '');
  const [rechts, setRechts] = useState(b?.vergleich?.b?.datum || '');
  const [ansicht, setAnsicht] = useState('vorne');
  useEffect(() => { setLinks(b?.vergleich?.a?.datum || ''); setRechts(b?.vergleich?.b?.datum || ''); }, [b]);
  if (!b || !b.frei) return null;
  const l = b.daten.find((t) => t.datum === links), r = b.daten.find((t) => t.datum === rechts);
  const auswahl = (wert, setzen, label) => (
    <select value={wert} onChange={(e) => setzen(e.target.value)} className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm" aria-label={label}>
      {b.daten.map((t) => <option key={t.datum} value={t.datum}>{tag(t.datum, { day: '2-digit', month: '2-digit', year: '2-digit' })}{t.im_zeitraum ? '' : ' (außerhalb)'}</option>)}
    </select>
  );
  return (
    <Card data-testid="fortschritt-bilder">
      <CardHeader className="pb-2"><CardTitle className="text-base">Bildvergleich</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {b.daten.length === 0 ? <p className="text-xs text-muted-foreground">Noch keine Fortschrittsfotos. Fotos lädt nur das Mitglied in der App hoch.</p> : <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {auswahl(links, setLinks, 'Linkes Datum')}<span className="text-muted-foreground">vs.</span>{auswahl(rechts, setRechts, 'Rechtes Datum')}
            <span className="ml-auto flex gap-1">{b.ansichten.map((a) => <Pille key={a} an={ansicht === a} onClick={() => setAnsicht(a)}>{{ vorne: 'Vorne', seite: 'Seitlich', hinten: 'Hinten' }[a]}</Pille>)}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[l, r].map((t, i) => (
              <div key={i} className="space-y-1">
                <Foto client={client} leadId={leadId} fid={t?.fotos?.[ansicht]} alt={`Fortschrittsfoto ${ansicht} vom ${t ? tag(t.datum) : ''}`} />
                <p className="text-xs font-medium">{t ? tag(t.datum, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</p>
              </div>
            ))}
          </div>
        </>}
      </CardContent>
    </Card>
  );
}

/* ── Die Seite ── */
export default function KundeFortschritt({ leadId, client = crmFortschrittClient, fuerTrainer = true, coachStil = false }) {
  const [zeitraum, setZeitraum] = useState('12w');
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(false);
  const laden = useCallback(async () => {
    setLaedt(true);
    try { setD((await client.laden(leadId, { zeitraum })).data); setFehler(''); }
    catch (e) { setFehler(fehlertext(e, 'Fortschritt konnte nicht geladen werden')); }
    finally { setLaedt(false); }
  }, [client, leadId, zeitraum]);
  useEffect(() => { laden(); }, [laden]);

  const kopf = (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Zeitraum">
      {ZEITRAEUME.map(([w, n]) => <Pille key={w} an={zeitraum === w} onClick={() => setZeitraum(w)} label={d?.zeitraum?.wahlen?.find((x) => x.wert === w)?.name}>{n}</Pille>)}
      {d && <span className="text-xs text-muted-foreground">{tag(d.zeitraum.von, { day: '2-digit', month: '2-digit', year: 'numeric' })} – {tag(d.zeitraum.bis, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>}
      {laedt && d && <span className="text-xs text-muted-foreground">lädt …</span>}
    </div>
  );
  if (fehler && !d) return <div className="space-y-3">{kopf}<Card><CardContent className="py-6 text-sm text-destructive" role="alert">{fehler}</CardContent></Card></div>;
  if (!d) return <div className="space-y-3">{kopf}<Skeleton className="h-20 w-full" /><Skeleton className="h-64 w-full" /><Skeleton className="h-48 w-full" /></div>;
  const messwerte = d.rechte.messwerte;
  const v = d.verlaeufe;
  return (
    <CoachStil.Provider value={coachStil}>
    <div className="space-y-4" data-testid="kunde-fortschritt">
      {kopf}
      {fehler && <p className="text-xs text-destructive" role="alert">{fehler}</p>}
      <Statusleiste s={d.status} messwerte={messwerte} />
      {!messwerte && <p className="text-xs text-muted-foreground">Gewicht, Umfänge, Körperwerte, Ernährung, Check-ins und Fotos sind Gesundheitsdaten und nur mit dem Ernährungs-Recht sichtbar.</p>}
      {messwerte && <GewichtChart g={v.gewicht} zeitraum={d.zeitraum} mitTrend={fuerTrainer && d.rechte.trend} />}
      <div className="grid gap-4 lg:grid-cols-2">
        <Bilanz bilanz={d.bilanz} hinweis={d.bilanz_hinweis} />
        <Heatmap h={d.heatmap} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <TrainingWochen wochen={v.training} />
        {messwerte && <ErnaehrungWochen wochen={v.ernaehrung} />}
      </div>
      {messwerte && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Linien testId="fortschritt-umfaenge" titel="Umfänge" einheit="cm" leer="Keine Umfänge in diesem Zeitraum."
            reihen={(v.umfaenge || []).map((u) => ({ name: u.name, punkte: u.punkte }))} />
          <Linien testId="fortschritt-koerper" titel="Körperfett & Muskelmasse (InBody)" einheit="" leer="Keine InBody-Messung in diesem Zeitraum."
            reihen={[{ name: 'Körperfett (%)', punkte: (v.koerper || []).filter((k) => k.koerperfett_prozent != null).map((k) => ({ datum: k.datum, wert: k.koerperfett_prozent })) },
              { name: 'Muskelmasse (kg)', punkte: (v.koerper || []).filter((k) => k.muskelmasse_kg != null).map((k) => ({ datum: k.datum, wert: k.muskelmasse_kg })) }]} />
        </div>
      )}
      {messwerte && <Bildvergleich b={d.bilder} client={client} leadId={leadId} />}
    </div>
    </CoachStil.Provider>
  );
}
